import { useAppStore } from '@/store/useAppStore';
import type { GPAVariant } from '@gpa-scales/shared-types';

const EMPTY_VARIANTS: readonly GPAVariant[] = [];

function ClipboardIcon() {
  return (
    <svg className="mr-1.5 inline h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
}

export function GPAReportingGuide() {
  const variants = useAppStore((s) => s.result?.variants ?? EMPTY_VARIANTS);

  const getVariant = (label: string) => variants.find((v) => v.label === label);

  const ucCapped = getVariant('UC Capped Weighted GPA');
  const commonApp = getVariant('Common App Weighted GPA');
  const unweighted = getVariant('Unweighted GPA');

  if (variants.length === 0) {
    return (
      <section className="px-1 py-2">
        <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
          <ClipboardIcon />
          GPA Reporting Guide
        </h3>
        <p className="text-sm text-[var(--color-text-muted)]">
          Add courses to see which GPA to report on each application.
        </p>
      </section>
    );
  }

  return (
    // Bolder: demoted from full card chrome to an inline footnote-style
    // section. The hero GPA card above now owns the visual weight; this
    // supporting reference stays readable without competing for attention.
    <section className="px-1 py-2">
      <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
        <ClipboardIcon />
        GPA Reporting Guide
      </h3>
      <div className="space-y-2 text-sm">
        <div className="flex flex-col gap-0.5 border-l-2 border-l-primary/50 pl-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">UC Application</p>
          <p className="text-xs text-[var(--color-text-secondary)]">
            Report your <strong>UC Capped Weighted GPA</strong> ({ucCapped ? ucCapped.value.toFixed(2) : '—'}).
            This is the GPA the UC system calculates for admissions eligibility.
          </p>
        </div>
        <div className="flex flex-col gap-0.5 border-l-2 border-l-success/50 pl-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-success">Common Application</p>
          <p className="text-xs text-[var(--color-text-secondary)]">
            Report your <strong>Weighted GPA</strong> on a <strong>5.0 scale</strong> ({commonApp ? commonApp.value.toFixed(2) : '—'}).
            Select your school's weighting when prompted.
          </p>
        </div>
        <div className="flex flex-col gap-0.5 border-l-2 border-l-info/50 pl-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-info">Coalition Application</p>
          <p className="text-xs text-[var(--color-text-secondary)]">
            Report your <strong>Unweighted GPA</strong> on a 4.0 scale ({unweighted ? unweighted.value.toFixed(2) : '—'}),
            unless your school reports a weighted GPA.
          </p>
        </div>
      </div>
    </section>
  );
}
